import React from "react";
import { motion } from "framer-motion";
import { BtnSignature } from "./BtnSignature";
import { StatusChip } from "./StatusChip";
import { WorkflowBar13 } from "./WorkflowBar13";
import { SilhouetteVehicle } from "./SilhouetteVehicle";
import { Clock, MapPin } from "lucide-react";

/**
 * HeroUrgent V2 — signature visuelle absolue du dashboard.
 * Ratio cinématique 21:9, photo immersive, gradient extrêmement subtil.
 * Colonne d'action noire à droite, CTA lime dominant.
 */
export function HeroUrgent({ dossier, onAction, testid }) {
  const {
    vehicle,
    client,
    plate,
    action,
    reason,
    image,
    completed = 9,
    silhouette = "coupe",
    dueLabel = "Créneau demandé depuis 2 h",
    location = "Retrait boutique · Paris 8e",
  } = dossier;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testid}
      className="shadow-wyvio-elev group relative overflow-hidden rounded-[28px] bg-[#151515]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Photo véhicule — full-width mobile, 7/12 desktop */}
        <div className="relative col-span-1 lg:col-span-7 aspect-[21/10] overflow-hidden">
          {image ? (
            <img
              src={image}
              alt={vehicle}
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
            />
          ) : (
            <SilhouetteVehicle
              type={silhouette}
              variant="dark"
              hint={false}
              className="h-full w-full"
            />
          )}
          {/* Gradients ultra-subtils */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(10,10,11,0) 55%, rgba(10,10,11,0.55) 85%, rgba(10,10,11,1) 100%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,10,11,0.35) 0%, rgba(10,10,11,0) 25%, rgba(10,10,11,0) 65%, rgba(10,10,11,0.25) 100%)",
            }}
          />

          {/* Badge en haut à gauche */}
          <div className="absolute left-8 top-8">
            <StatusChip
              status="urgent"
              label="Prochaine urgence"
              className="!bg-black/45 !text-white backdrop-blur-md"
            />
          </div>

          {/* Plaque + client en bas à gauche */}
          <div className="absolute bottom-8 left-8 text-white">
            <p className="wyvio-tabular text-[11px] font-medium uppercase tracking-[0.24em] text-white/60">
              {plate}
            </p>
            <p className="mt-2 text-[13.5px] font-normal text-white/85">
              {client}
            </p>
          </div>
        </div>

        {/* Colonne d'action — full-width mobile, 5/12 desktop */}
        <div className="col-span-1 lg:col-span-5 flex flex-col justify-between p-8 lg:p-12 text-white">
          <div>
            <p className="text-[10.5px] font-medium uppercase tracking-[0.24em] text-white/45">
              À faire maintenant
            </p>
            <h2 className="mt-4 line-clamp-2 text-[42px] font-medium leading-[1.02] tracking-[-0.028em]">
              {vehicle}
            </h2>
            <p className="mt-6 text-[15px] font-normal leading-[1.55] text-white/70">
              {reason}
            </p>

            <div className="mt-10 space-y-4 text-[13px] font-normal text-white/70">
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-white/40" strokeWidth={1.6} />
                <span>{dueLabel}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-white/40" strokeWidth={1.6} />
                <span>{location}</span>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <WorkflowBar13 completed={completed} compact variant="lime" className="mb-8" />
            <BtnSignature
              variant="lime"
              size="lg"
              arrow
              onClick={onAction}
              testid={`${testid}-cta`}
              className="w-full"
            >
              {action}
            </BtnSignature>
            <p className="mt-4 text-center text-[11.5px] font-normal text-white/35">
              Un clic — trois créneaux sont proposés au client
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
